import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import "./Transactionhistory.css";

const Transactionhistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingButtons, setLoadingButtons] = useState({});

  const baseUrl = process.env.NODE_ENV === "development" 
    ? process.env.REACT_APP_BACKEND_LOCAL_API 
    : process.env.REACT_APP_BACKEND_LIVE_API;

  const showToast = (message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `toast-notification ${type}`;
    toast.style.position = "fixed";
    toast.style.bottom = "20px";
    toast.style.right = "-300px";
    toast.style.padding = "12px 24px";
    toast.style.borderRadius = "4px";
    toast.style.color = "white";
    toast.style.backgroundColor = 
      type === "success" ? "#4CAF50" : 
      type === "error" ? "#F44336" :
      type === "warning" ? "#FF9800" :
      type === "info" ? "#2196F3" : 
      type === "danger" ? "#DC3545" : "#4CAF50";
    toast.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)";
    toast.style.transition = "right 0.3s ease-out";
    toast.style.zIndex = "9999";
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.right = "20px";
    }, 10);

    setTimeout(() => {
      toast.style.right = "-300px";
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const params = {
        page: pageNumber,
        _limit: itemsPerPage,
        _q: searchQuery
      };
      
      if (statusFilter !== "all") {
        params._status = statusFilter;
      }

      const response = await axios.get(`${baseUrl}txn/deposit/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setTransactions(response.data.admin || []);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusCheck = async (gateway, orderId, orderToken, transactionId) => {
    try {
      setLoadingButtons(prev => ({ ...prev, [transactionId]: { type: "verify", loading: true } }));
      
      const token = localStorage.getItem("token");
      let endpoint = "";
      
      switch(gateway) {
        case "razorpay": endpoint = "razorpaycheck/response"; break;
        case "decentropay": endpoint = "decentroupipay/response"; break;
        case "pinelab": endpoint = "pinelabdesposit/response"; break;
        case "upigateway": endpoint = "depositupipay/response"; break;
        default: endpoint = "depositupipay/response";
      }

      const response = await axios.post(`${baseUrl}${endpoint}`, { order_id: orderId, order_token: orderToken }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the specific transaction in state
      setTransactions(prev => prev.map(txn => 
        txn._id === transactionId ? { ...txn, status: response.data.status || "PAID" } : txn
      ));
      
      showToast("Transaction verified successfully", "success");
    } catch (error) {
      showToast(error.response?.data?.message || "Verification failed", "error");
    } finally {
      setLoadingButtons(prev => ({ ...prev, [transactionId]: { type: "verify", loading: false } }));
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      setLoadingButtons(prev => ({ ...prev, [id]: { type: status === "SUCCESS" ? "approve" : "reject", loading: true } }));
      
      const token = localStorage.getItem("token");
      await axios.post(`${baseUrl}userdipositupdate/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the specific transaction in state
      setTransactions(prev => prev.map(txn => 
        txn._id === id ? { ...txn, status: status === "SUCCESS" ? "PAID" : "FAILED" } : txn
      ));
      
      const message = status === "SUCCESS" 
        ? "Transaction approved successfully" 
        : "Transaction rejected successfully";
      
      showToast(message, status === "SUCCESS" ? "success" : "danger");
    } catch (error) {
      showToast("Failed to update status", "error");
    } finally {
      setLoadingButtons(prev => ({ ...prev, [id]: { type: status === "SUCCESS" ? "approve" : "reject", loading: false } }));
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      setLoadingButtons(prev => ({ ...prev, [id]: { type: "delete", loading: true } }));
      
      const token = localStorage.getItem("token");
      await axios.delete(`${baseUrl}txn/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove the transaction from state
      setTransactions(prev => prev.filter(txn => txn._id !== id));
      
      showToast("Transaction deleted successfully", "info");
    } catch (error) {
      showToast(error.response?.data?.message || "Failed to delete transaction", "error");
    } finally {
      setLoadingButtons(prev => ({ ...prev, [id]: { type: "delete", loading: false } }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0
    }).format(amount);
  };

  useEffect(() => {
    fetchTransactions();
  }, [pageNumber, itemsPerPage, searchQuery, statusFilter]);

  return (
    <div className="container-fluid py-3 py-md-4 lp-deposit-history">
      <div className="card border-0 lp-deposit-card shadow-sm">
        <div className="card-body p-3 p-md-4">
          <h4 className="lp-deposit-title d-flex align-items-center gap-2 mb-3 mb-md-4">
            <span className="lp-deposit-title-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </span>
            Deposit history
          </h4>

          <div className="lp-deposit-filters">
            <div className="flex-grow-1" style={{ minWidth: "160px", maxWidth: "360px" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by name…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="lp-deposit-filter-pills">
              <button type="button" className={`btn btn-sm ${statusFilter === "all" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setStatusFilter("all")}>All</button>
              <button type="button" className={`btn btn-sm ${statusFilter === "PAID" ? "btn-success" : "btn-outline-success"}`} onClick={() => setStatusFilter("PAID")}>Paid</button>
              <button type="button" className={`btn btn-sm ${statusFilter === "Pending" ? "btn-warning" : "btn-outline-warning"}`} onClick={() => setStatusFilter("Pending")}>Pending</button>
              <button type="button" className={`btn btn-sm ${statusFilter === "FAILED" ? "btn-danger" : "btn-outline-danger"}`} onClick={() => setStatusFilter("FAILED")}>Failed</button>
            </div>
          </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                   
                  </div>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p className="mb-0 small">No transactions found.</p>
                </div>
              ) : (
                <div className="lp-deposit-table-wrap">
                  <table className="table table-hover align-middle table-sm mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>User</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn, index) => {
                        const isButtonLoading = loadingButtons[txn._id]?.loading || false;
                        const buttonType = loadingButtons[txn._id]?.type || "";
                        const initial = (txn.User_id?.Name || "?").charAt(0).toUpperCase();
                        
                        return (
                          <tr key={txn._id}>
                            <td>{index + 1 + (pageNumber - 1) * itemsPerPage}</td>
                            <td>
                              {txn.User_id ? (
                                <Link 
                                  to={`/user/view_user/${txn.User_id._id}`}
                                  className="text-decoration-none lp-deposit-user-link"
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="lp-avatar">{initial}</span>
                                    <div>
                                      <div className="fw-medium small">{txn.User_id.Name || "—"}</div>
                                      <small className="text-muted" style={{ fontSize: "0.7rem" }}>{txn.User_id.Phone}</small>
                                    </div>
                                  </div>
                                </Link>
                              ) : "N/A"}
                            </td>
                            <td className="fw-semibold">{formatAmount(txn.amount)}</td>
                            <td>
                              <span className={`badge rounded-pill ${
                                txn.status === "PAID" ? "bg-success" :
                                txn.status === "FAILED" ? "bg-danger" : "bg-warning text-dark"
                              }`} style={{ fontSize: "0.65rem" }}>
                                {txn.status}
                              </span>
                            </td>
                            <td className="text-muted small">{formatDate(txn.createdAt)}</td>
                            <td>
                              {txn.status === "Pending" ? (
                                <div className="lp-deposit-actions">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handleStatusCheck(
                                      txn.payment_gatway,
                                      txn.order_id,
                                      txn.order_token,
                                      txn._id
                                    )}
                                    disabled={isButtonLoading && buttonType === "verify"}
                                  >
                                    {isButtonLoading && buttonType === "verify" ? (
                                      <span className="spinner-border spinner-border-sm" role="status" />
                                    ) : (
                                      <>Verify</>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    onClick={() => handleStatusUpdate(txn._id, "SUCCESS")}
                                    disabled={isButtonLoading && buttonType === "approve"}
                                  >
                                    {isButtonLoading && buttonType === "approve" ? (
                                      <span className="spinner-border spinner-border-sm" role="status" />
                                    ) : (
                                      <>Approve</>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleStatusUpdate(txn._id, "FAILED")}
                                    disabled={isButtonLoading && buttonType === "reject"}
                                  >
                                    {isButtonLoading && buttonType === "reject" ? (
                                      <span className="spinner-border spinner-border-sm" role="status" />
                                    ) : (
                                      <>Fail</>
                                    )}
                                  </button>
                                </div>
                              ) : (
                                <div className="lp-deposit-actions">
                                  {txn.status === "FAILED" && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDeleteTransaction(txn._id)}
                                      disabled={isButtonLoading && buttonType === "delete"}
                                    >
                                      {isButtonLoading && buttonType === "delete" ? (
                                        <span className="spinner-border spinner-border-sm" role="status" />
                                      ) : (
                                        <>Delete</>
                                      )}
                                    </button>
                                  )}
                                  <span className="text-muted small">—</span>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3 lp-withdraw-pager" style={{ overflowX: "auto" }}>
                  <ReactPaginate
                    previousLabel="‹"
                    nextLabel="›"
                    breakLabel="…"
                    pageCount={totalPages}
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={3}
                    onPageChange={({ selected }) => setPageNumber(selected + 1)}
                    containerClassName="pagination pagination-sm justify-content-center mb-0 flex-nowrap"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    activeClassName="active"
                    forcePage={pageNumber - 1}
                  />
                </div>
              )}
        </div>
      </div>
    </div>
  );
};

export default Transactionhistory;  